import { Injectable } from '@angular/core';
import { ModalService } from './modal.service';
import { SnackbarService } from './snackbar.service';
import { ErrorService } from './error.service';
import { SpinnerService } from './spinner.service';
import { FileService } from './file.service';
import { TrackParcelService } from './track-parcel.service';
import { ParcelService } from './parcel.service';
import { Observable } from 'rxjs';

/**
 * Parcel Utility Service
 * 
 * This service provides common functionality for email and print operations on parcels
 * that was previously duplicated across multiple components.
 */
@Injectable({
  providedIn: 'root'
})
export class ParcelUtilityService {
  constructor(
    private _modalService: ModalService,
    private _snackbarService: SnackbarService,
    private _errorService: ErrorService,
    private _spinnerService: SpinnerService,
    private _fileService: FileService,
    private _trackParcelService: TrackParcelService,
    private _parcelService: ParcelService
  ) { }

  /**
   * Email a parcel with its details
   * @param parcelId The ID of the parcel to email
   */
  public emailParcel(parcelId: string): void {
    const dialogRef = this._modalService.displayEmailParcelDetailsModal(parcelId, 'name', 'DETAILED');
    dialogRef.afterClosed().subscribe(
      (result) => {
        if (result) {
          this._snackbarService.openSnackBar('Email Sent', 'Close', 5000, 'success');
        }
      }, 
      (error) => {
        this._errorService.error(error, 'Emailing shipping label failed', 'modal');
      }
    );
  }

  /**
   * Print a parcel's details as PDF
   * @param parcelId The ID of the parcel to print
   */
  public printParcel(parcelId: string): void {
    this._spinnerService.startSpinner();
    this._trackParcelService.generateParcelDetails(parcelId).subscribe(
      (response: any) => {
        const file = new Blob([response], {type: 'application/pdf'});
        if ((window.navigator as any).msSaveOrOpenBlob) { // IE and Edge functionality
          (window.navigator as any).msSaveOrOpenBlob(file, 'Parcel Details-' + parcelId + '.pdf');
        } else {
          this._fileService.openFileNewWindow(file);
        }
        this._spinnerService.stopSpinner();
      },
      (error) => {
        this._spinnerService.stopSpinner();
        if (error.error instanceof Blob) {
          const file = new Blob([error.error], {type: error.error.type});
          const fr = new FileReader();
          fr.onload = (e: any) => {
            if (error.error.type === 'application/json') {
              error = JSON.parse(e.target.result);
            }
            this._errorService.error(error, 'Print Parcel Failed', 'modal');
          };
          fr.readAsText(file);
        } else {
          this._errorService.error(error, 'Print Parcel Failed', 'modal');
        }
      }
    );
  }

  /**
   * Print all parcel details for an order as PDF
   * @param orderIdOrRouteId The order id or route id
   * @param isOrderOnDemand Whether the order is on-demand (default: true)
   */
  public printAllParcelDetails(orderIdOrRouteId: string, isOrderOnDemand: boolean, postDate: string | null): void {
    this._spinnerService.startSpinner();
    this._parcelService.printAllParcelsForOrderOrRoute(orderIdOrRouteId, isOrderOnDemand, postDate)
      .subscribe(
        (response: any) => {
          const file = new Blob([response], {type: 'application/pdf'});
          if ((window.navigator as any).msSaveOrOpenBlob) { // IE and Edge functionality
            (window.navigator as any).msSaveOrOpenBlob(file, 'AllParcelDetails' + '.pdf');
          } else {
            this._fileService.openFileNewWindow(file);
          }
          this._spinnerService.stopSpinner();
        },
        (error) => {
          this._spinnerService.stopSpinner();
          if (error.error instanceof Blob) {
            const file = new Blob([error.error], {type: error.error.type});
            const fr = new FileReader();
            fr.onload = (e: any) => {
              if (error.error.type === 'application/json') {
                error = JSON.parse(e.target.result);
              }
              this._errorService.error(error, 'printAllParcelDetails Failed', 'modal');
            };
            fr.readAsText(file);
          } else {
            this._errorService.error(error, 'printAllParcelDetails Failed', 'modal');
          }
        }
      );
  }

  /**
   * Print all parcel details for an order as PDF
   * @param routeStopId The route stop id
   */
  public printRouteStopParcelDetails(routeStopId: string): void {
    this._spinnerService.startSpinner();
    this._trackParcelService.generateRouteStopParcelDetails(routeStopId)
      .subscribe(
        (response: any) => {
          const file = new Blob([response], {type: 'application/pdf'});
          if ((window.navigator as any).msSaveOrOpenBlob) { // IE and Edge functionality
            (window.navigator as any).msSaveOrOpenBlob(file, 'RouteStopParcelDetails' + '.pdf');
          } else {
            this._fileService.openFileNewWindow(file);
          }
          this._spinnerService.stopSpinner();
        },
        (error) => {
          this._spinnerService.stopSpinner();
          if (error.error instanceof Blob) {
            const file = new Blob([error.error], {type: error.error.type});
            const fr = new FileReader();
            fr.onload = (e: any) => {
              if (error.error.type === 'application/json') {
                error = JSON.parse(e.target.result);
              }
              this._errorService.error(error, 'printRouteStopParcelDetails Failed', 'modal');
            };
            fr.readAsText(file);
          } else {
            this._errorService.error(error, 'printRouteStopParcelDetails Failed', 'modal');
          }
        }
      );
  }

  /**
   * Email all parcel details for an order
   * @param orderId The order id
   * @param isOrderOnDemand Whether the order is on-demand (default: true)
   */
  public emailAllParcelDetails(orderId: string, isOrderOnDemand: boolean = true, postDate: string | null): void {
    const others = {
      isOrderOnDemand: isOrderOnDemand,
      postDate: postDate
    };

    const dialogRef = this._modalService.displayEmailParcelDetailsModal(orderId, 'name', 'EMAIL_PARCELS_FOR_ORDER', null, others);

    dialogRef.afterClosed().subscribe(
      (result) => {
        if (result) {
          this._snackbarService.openSnackBar('Email Sent', 'Close', 5000, 'success');
        }
      }, 
      (error) => {
        this._errorService.error(error, 'Emailing parcel results failed', 'modal');
      }
    );
  }

  /**
   * Email all parcel details for an order
   * @param routeStopId The route stop id
   */
  public emailRouteStopParcelsDetails(routeStopId: string): void {

    const dialogRef = this._modalService.displayEmailParcelDetailsModal(routeStopId, 'name', 'EMAIL_PARCELS_FOR_ROUTE_STOP', null, null);

    dialogRef.afterClosed().subscribe(
      (result) => {
        if (result) {
          this._snackbarService.openSnackBar('Email Sent', 'Close', 5000, 'success');
        }
      }, 
      (error) => {
        this._errorService.error(error, 'Emailing parcel results failed', 'modal');
      }
    );
  }
}
